import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar, AlertTriangle } from "lucide-react";

interface WeekendConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (inclureWeekend: boolean) => void;
  dateFin: Date;
  type?: "membre" | "equipe";
}

/**
 * Dialogue de confirmation pour les dates de fin tombant un weekend
 * Demande à l'utilisateur si le samedi/dimanche doit être compté comme jour travaillé
 */
const WeekendConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  dateFin,
  type = "membre",
}: WeekendConfirmDialogProps) => {
  const dayOfWeek = dateFin.getDay();
  const jourNom = dayOfWeek === 0 ? "dimanche" : "samedi";
  const dateFormatee = dateFin.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const sujet = type === "equipe" ? "l'équipe" : "l'ouvrier";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <DialogTitle>Date de fin en weekend</DialogTitle>
              <DialogDescription className="mt-1">
                Travail le {jourNom} ?
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg mb-4">
            <Calendar className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm font-medium">{dateFormatee}</span>
          </div>

          <p className="text-sm text-muted-foreground">
            La date de fin sélectionnée tombe un <strong>{jourNom}</strong>.
            Est-ce que {sujet} travaillera ce jour-là ?
          </p>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onConfirm(false)}
            className="flex-1"
          >
            Non, jour non travaillé
          </Button>
          <Button
            onClick={() => onConfirm(true)}
            className="flex-1 bg-primary"
          >
            Oui, compter ce jour
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WeekendConfirmDialog;

/**
 * Hook utilitaire pour détecter si une date tombe un weekend
 */
export const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6; // 0 = dimanche, 6 = samedi
};

/**
 * Fonction de calcul des jours ouvrés avec option d'inclure les weekends
 */
export const calculateJoursOuvresWithWeekend = (
  debut: Date,
  fin: Date,
  inclureWeekendFin: boolean = false
): number => {
  let count = 0;
  const current = new Date(debut);
  const finDate = new Date(fin);

  while (current <= finDate) {
    const dayOfWeek = current.getDay();
    const isLastDay = current.getTime() === finDate.getTime();

    // Compter si c'est un jour de semaine (lun-ven)
    // OU si c'est le dernier jour ET qu'on inclut le weekend
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    } else if (isLastDay && inclureWeekendFin) {
      count++;
    }

    current.setDate(current.getDate() + 1);
  }

  return count;
};
